import * as React from "react";
import { MultiSelect } from "../../../forms/informedv2/MultiSelect";
import { Form, useFormApi, Checkbox, Option, useFormState } from "informed";
import { TextBox } from "../../../forms/informedv2/TextBox";
import Alert, { AlertCategoryEnum } from "../../../misc/components/Alert";
import LoadingWithMessage from "../../../misc/components/LoadingWithMessage";
import { ParameterCategoryType, StudyLungVolumeSetting, UnitType } from "../../../misc/api/apiTypes";
import { Link, useParams } from "react-router-dom";
import { AuditModal, AuditForm } from "../../../auditmodal/components/AuditModal";
import { ActionButtons } from "../../../misc/ActionButtons";
import { IntegerValidator, MinMaxValidator } from "../../../forms/validation/NumericValidators";
import { ParameterEntity } from "../../../misc/api/SerenityReferenceData";
import { SessionTypes } from "../../../misc/api/ApiSessionTypes";
import { ConvertToSelectListItemArray } from "./ParameterSelectList";
import { useCookieRedirect } from "../../../misc/CookieRedirect";
import { useCurrentStudyConfigurationContext } from "../../../Core/CurrentStudyConfigurationContext";
import { useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";
import { ISelectListItem } from "../../../misc/AspnetSelectList";

declare const rootUrl: string;

interface LungVolumeSettingsScreenFormState {
    acceptableTests: number;
    maximumTests: number;
    ervTolerance: number;
    selectedTestParameters: ISelectListItem[];
    overreadEnabled: boolean;
    selectedAnalysisParameters: ISelectListItem[];
    selectedRepeatabilityParameters: ISelectListItem[];
}

interface LungVolumeSettingsScreenFormProps {
    studyId: string;
    parameters: ParameterEntity[];
    sessionType: SessionTypes;
}

export const LungVolumeSettingsScreenForm = ({ studyId, parameters, sessionType }: LungVolumeSettingsScreenFormProps) => {
    const formApi = useFormApi<LungVolumeSettingsScreenFormState>();
    const formState = useFormState<LungVolumeSettingsScreenFormState>();
    const [unitTypes, setUnitTypes] = React.useState<UnitType[]>([]);
    const [submitting, setSubmitting] = React.useState(false);
    const [alertShown, setAlertShown] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [auditModalVisible, setAuditModalVisible] = React.useState(false);
    const { refreshSettings } = useCurrentStudyConfigurationContext();
    const legacyClient = useWebPortalLegacyClient();

    const parametersSelectList: ISelectListItem[] = parameters.map(p => {
        return {
            Id: p.Id,
            Value: p.Keyword,
            Text: p.DisplayName + (p.UnitName ? ` (${p.UnitName})` : ''),
            Group: null,
            Selected: false,
            Disabled: false
        };
    });

    const analysisParametersSelectList: ISelectListItem[] = parameters.filter(p => p.Category === ParameterCategoryType.Default || p.Category === ParameterCategoryType.AnalysisParameter).map(p => {
        return {
            Id: p.Id,
            Value: p.Keyword,
            Text: p.DisplayName + (p.UnitName ? ` (${p.UnitName})` : ''),
            Group: null,
            Selected: false,
            Disabled: false
        };
    });

    const repeatableParametersSelectList: ISelectListItem[] = parameters.filter(p => p.Category === ParameterCategoryType.Default || p.Category === ParameterCategoryType.RepeatableParameter).map(p => {
        return {
            Id: p.Id,
            Value: p.Keyword,
            Text: p.DisplayName + (p.UnitName ? ` (${p.UnitName})` : ''),
            Group: null,
            Selected: false,
            Disabled: false
        };
    });

    const onPreAuditSubmit = () => {
        setErrorAlert(null);
        setSubmitting(true);
        formApi.validate();

        const state = formApi.getState();

        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        setSubmitting(false);
        setAuditModalVisible(true);
    }

    const onSubmit = async (auditForm: AuditForm) => {
        setSubmitting(true);
        setAuditModalVisible(false);
        setErrorAlert(null);

        formApi.validate();
        const state = formApi.getState();

        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        const lungVolumeSettings: StudyLungVolumeSetting = {
            SelectedTestParameters: convertToIdsArray(state.values.selectedTestParameters),
            AcceptableTests: state.values.acceptableTests,
            MaxTests: state.values.maximumTests,
            AcceptableERVTolerance: state.values.ervTolerance,
            IsOverreadingAvailable: state.values.overreadEnabled,
            SelectedAnalysisParameters: convertToIdsArray(state.values.selectedAnalysisParameters),
            SelectedRepeatabilityParameters: convertToIdsArray(state.values.selectedRepeatabilityParameters),
        }

        const payload = JSON.stringify(lungVolumeSettings);

        try {
            const result = sessionType === SessionTypes.LungVolumeHelium ?
                await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.LV.StudyLungVolumeHeliumSetting", payload)
                : await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.LV.StudyLungVolumePlethSetting", payload);

            if (result) {
                setAlertShown(true);
                refreshSettings();
            }
        } catch (ex) {
            setErrorAlert(ex.Message);
        }

        setSubmitting(false);
    };

    const convertToIdsArray = (params: ISelectListItem[]) => {
        return params.length > 0
            ? parameters.filter(p => params.map(s => s.Value).includes(p.Keyword)).map(p => p.Id)
            : null;
    }

    const AcceptableToMaximumValidator = (value: number, threshold: number) => {
        return !value || value > threshold ? "'Acceptable Tests' field cannot be greater than the 'Maximum Tests' field" : undefined;
    }

    const acceptableTestValidation = (value: number, values: LungVolumeSettingsScreenFormState) => {
        return IntegerValidator(value) || MinMaxValidator(value, 1, 8) || AcceptableToMaximumValidator(values.acceptableTests, values.maximumTests)
    }

    const maximumTestValidation = (value: number) => {
        return IntegerValidator(value) || MinMaxValidator(value, 4, 8);
    }

    const ervToleranceValidation = (value: number) => {
        return IntegerValidator(value) || MinMaxValidator(value, 0, 100);
    }

    const maximum3Decimals = (value: number) => {
        return value === Math.floor(value) ? undefined : value.toString().split(".")[1].length > 3 ? "A maximum of 3 decimal places is supported" : undefined;
    }

    const testParametersOptionRenderer = (option) => {
        if (option.DisplayName === "Select All")
            return <b>{option.DisplayName}</b>;

        return (
            <>{option.DisplayName}</>
        );
    }

    const typeName = sessionType === SessionTypes.LungVolumePleth ? "Plethysmography" : "Helium Dilution"

    return (
        <>
            <Alert show={alertShown} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to="/studies">Studies list</Link>?
            </Alert>
            <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <div className="row">
                <div className="col-md-12">
                    <h2 className="text-primary">Lung Volume ({typeName}) Settings</h2>
                    <div className="panel panel-default">
                        <div className="panel-heading">
                            <h3 className="panel-title">Session Configuration</h3>
                        </div>
                        <div className="panel-body">
                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>Acceptable Tests</label>
                                    <p className="help-block">Configures the number of acceptable tests required to comprise a valid session</p>
                                    <TextBox<number, LungVolumeSettingsScreenFormState> id="acceptableTests" field="acceptableTests" type="number" min="1" max="8" required validate={acceptableTestValidation} />
                                </div>
                                <div className="form-group col-md-6">
                                    <label>Maximum Tests</label>
                                    <p className="help-block">Configures the maximum number of tests allowed</p>
                                    <TextBox<number, LungVolumeSettingsScreenFormState> id="maximumTests" field="maximumTests" type="number" min="4" max="8" required validate={maximumTestValidation} />
                                </div>
                            </div>

                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>Displayed Test Parameters</label>
                                    <p className="help-block">The test parameters to be displayed on subject testing screens for supported applications</p>
                                    <MultiSelect
                                        id="selectedTestParameters"
                                        field="selectedTestParameters"
                                        options={parametersSelectList}
                                        labelKey="Text"
                                        valueKey="Value"
                                        multi
                                        //optionRenderer={testParametersOptionRenderer}
                                        //valueRenderer={(value) => value.DisplayName}
                                        allowSelectAll
                                    />
                                </div>
                                <div className="form-group col-md-6">
                                    <label>Acceptable ERV Tolerance</label>
                                    <p className="help-block">Configures the Acceptable ERV Tolerance</p>
                                    <TextBox<number, LungVolumeSettingsScreenFormState> id="ervTolerance" field="ervTolerance" type="number" min="0" max="100" required validate={ervToleranceValidation} />
                                </div>
                            </div>

                        </div>
                    </div>

                    <div className="panel panel-default">
                        <div className="panel-heading">
                            <h3 className="panel-title">Overreading</h3>
                        </div>
                        <div className="panel-body">
                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>Over-Read enabled</label>
                                    <Checkbox id="overreadEnabledToggle" className="checkbox" field="overreadEnabled" />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>Analysis Parameters</label>
                                    <p className="help-block">The test parameters to be displayed in the over-reading details</p>
                                    <MultiSelect
                                        menuPlacement="auto"
                                        disabled={!formState.values.overreadEnabled}
                                        id="selectedAnalysisParameters"
                                        field="selectedAnalysisParameters"
                                        options={analysisParametersSelectList}
                                        labelKey="Text"
                                        valueKey="Value"
                                        multi
                                        allowSelectAll
                                    />
                                </div>
                                <div className="form-group col-md-6">
                                    <label>Repeatability Parameters</label>
                                    <p className="help-block">The repeatability parameters to be displayed in the over-reading details</p>
                                    <MultiSelect
                                        menuPlacement="auto"
                                        disabled={!formState.values.overreadEnabled}
                                        id="selectedRepeatabilityParameters"
                                        field="selectedRepeatabilityParameters"
                                        options={repeatableParametersSelectList}
                                        labelKey="Text"
                                        valueKey="Value"
                                        multi
                                        allowSelectAll
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <ActionButtons submitting={submitting} onSaveClick={() => onPreAuditSubmit()} automationIdPrefix="ActionsHeader" returnToListRoute="/studies" />
                        </div>
                    </div>
                </div>
            </div>
            {auditModalVisible ? (
                <AuditModal
                    header="Approval"
                    isSubmitting={submitting}
                    onClose={() => setAuditModalVisible(false)}
                    onSubmit={(audit) => onSubmit(audit)}
                />
            ) : null}
        </>
    );
}

export const LungVolumeSettings = ({ sessionType }) => {
    const { id } = useParams<{ id: string }>();
    const [initialValues, setInitialValues] = React.useState<LungVolumeSettingsScreenFormState>();
    const [parameters, setParameters] = React.useState([]);
    const legacyClient = useWebPortalLegacyClient();

    useCookieRedirect(id);

    React.useEffect(() => {
        const init = async () => {
            const hydrate = async (lungVolumeSettings: StudyLungVolumeSetting, lungVolumeParameters: ParameterEntity[]) => {
                const formState = {
                    acceptableTests: lungVolumeSettings.AcceptableTests,
                    maximumTests: lungVolumeSettings.MaxTests,
                    ervTolerance: lungVolumeSettings.AcceptableERVTolerance,
                    overreadEnabled: lungVolumeSettings.IsOverreadingAvailable,
                    selectedTestParameters: ConvertToSelectListItemArray(lungVolumeSettings.SelectedTestParameters, lungVolumeParameters),
                    selectedAnalysisParameters: ConvertToSelectListItemArray(lungVolumeSettings.SelectedAnalysisParameters, lungVolumeParameters),
                    selectedRepeatabilityParameters: ConvertToSelectListItemArray(lungVolumeSettings.SelectedRepeatabilityParameters, lungVolumeParameters)
                }

                setInitialValues(formState);
            }

            const params = await legacyClient.getParametersBySessionType(sessionType);
            setParameters(params);

            const lungVolumeSettings = sessionType === SessionTypes.LungVolumeHelium
                ? await legacyClient.getStudyLungVolumeHeliumSettings(id)
                : await legacyClient.getStudyLungVolumePlethSettings(id);

            hydrate(lungVolumeSettings, params);
        }

        init();
    }, [id]);

    if (!initialValues) {
        return <LoadingWithMessage message="Loading Lung Volume Settings..." />;
    }

    const validateFields = (values) => {
        return {
            selectedTestParameters: values.selectedTestParameters === null ? "At least one value must be selected" : undefined,
            selectedAnalysisParameters: values.selectedAnalysisParameters === null ? "At least one value must be selected" : undefined,
            selectedRepeatabilityParameters: values.selectedRepeatabilityParameters === null ? "At least one value must be selected" : undefined,
        }
    }

    return (
        <Form id="lungVolumeSettingsForm" noValidate initialValues={initialValues} validateFields={validateFields}>
            <LungVolumeSettingsScreenForm studyId={id} parameters={parameters} sessionType={sessionType} />
        </Form>
    );
}
